// ABOUTME: Locale-aware dateRange cell renderer for PIM data tables.
// ABOUTME: Formats date values to the user's locale and, in edit mode, opens the EditNode date picker.
import { Box, Popover } from '@ds/coolshop';
import {
    type CustomCellRendererProps,
    type NodeContentValueO,
    useCellEditCommit,
    useEditingCellReporter,
} from '@ds/data-table';
import DateRangeField from '@src/components/formFields/DateFieldElement';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { StandaloneField, writeLanguageEntry } from './StandaloneField';

export default function GridCellDateRangeRender(props: CustomCellRendererProps) {
    const { value, disabled, contentsValue, contexts, row, editableOpt, ct } = props;
    const intl = useIntl();

    const anchorRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [entries, setEntries] = useState(value as NodeContentValueO[]);

    const { commit, isLoading, isError } = useCellEditCommit({
        editableOpt,
        contentsValue,
        nodeId: row.id,
        hierarchy: row.hierarchy,
        ct,
        row,
    });
    // An open editor is a pending edit: another user's save of this cell becomes a conflict.
    useEditingCellReporter(
        row.id,
        contentsValue?.content
            ? `${contentsValue.content.contentTypeGroup}-${contentsValue.content.contentType}`
            : undefined,
        isOpen,
    );

    const first = entries?.[0];
    const dates = first?.value;

    // A cleared date is stored as an empty string (legacy ones as "NaN-NaN-NaN"): formatting those
    // would print "Invalid Date", so they are dropped and an entirely unset field renders as empty.
    const parseable = (Array.isArray(dates) ? dates : []).filter(
        date => !!date && !Number.isNaN(new Date(date as string).getTime()),
    );

    const formatted = parseable.map(date =>
        intl.formatDate(date as string, { year: 'numeric', month: '2-digit', day: '2-digit' }),
    );

    const content = formatted.length > 1 ? `${formatted[0]} - ${formatted[1]}` : formatted[0] ?? '';

    if (!editableOpt) {
        return content ? <Box color={disabled ? 'grey.500' : 'inherit'}>{content}</Box> : null;
    }

    // `enableRange` decides whether the field edits one date or two; the stored value follows it,
    // so the editor is seeded with as many entries as the range needs.
    const enableRange = ct?.enableRange ?? true;
    const storedDates = (Array.isArray(dates) ? dates : []).map(date => date ?? '');
    const initialValue = enableRange
        ? [storedDates[0] ?? '', storedDates[1] ?? '']
        : storedDates[0] ?? '';

    const errorColor = isError ? 'error.main' : 'inherit';
    const editorColor = isLoading || disabled ? 'grey.500' : errorColor;

    const handleSave = async (edited: unknown) => {
        const nextDates = Array.isArray(edited) ? edited : [edited];
        const nextEntries = writeLanguageEntry(
            entries ?? [],
            contexts.language.defaultLanguage,
            nextDates,
        );
        const saved = await commit({ values: nextEntries }, nextDates.filter(Boolean).join(' - '));
        if (saved) {
            setEntries(nextEntries);
            setIsOpen(false);
        }
    };

    return (
        <>
            <Box
                ref={anchorRef}
                onClick={e => {
                    if (isLoading) return;
                    e.stopPropagation();
                    setIsOpen(true);
                }}
                cursor={isLoading ? 'progress' : 'pointer'}
                height="70%"
                py={0.5}
                pl={0.5}
                color={editorColor}
            >
                {content}
            </Box>
            <Popover
                open={isOpen}
                anchorEl={anchorRef.current}
                onClose={() => setIsOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box
                    onClick={e => {
                        e.stopPropagation();
                    }}
                >
                    <StandaloneField
                        initialValue={initialValue}
                        label={intl.formatMessage({
                            id: 'search-detail.edit-cell.date',
                            defaultMessage: 'Date',
                        })}
                        saving={isLoading}
                        onCancel={() => setIsOpen(false)}
                        onSave={handleSave}
                    >
                        {fieldProps => (
                            <DateRangeField
                                {...(fieldProps as any)}
                                enableRange={enableRange}
                                allowPastDates={ct?.allowPastDates ?? false}
                            />
                        )}
                    </StandaloneField>
                </Box>
            </Popover>
        </>
    );
}
